/**
 * @file End-to-end orchestration for one export run. After the user has
 * approved on mobile and we've imported the KEK, this module:
 *
 *   1. fetches Family / Persons / Memberships / Entries / Observations / Digests
 *   2. unwraps each record's wrapped_dek with the KEK and decrypts text fields
 *   3. fetches each media file from Cloud Storage, decrypts, and emits a
 *      ZipEntry for the streaming zip writer
 *   4. composes data.json + schema.md + README.md and emits them as ZipEntries
 *   5. notifies the caller of progress through a callback
 *
 * All actual zip writing is delegated to `zipStream.ts`. This module is pure
 * orchestration so it can be unit-tested with mock Firestore / Storage.
 */

import type { Firestore } from "firebase/firestore";
import type { FirebaseStorage } from "firebase/storage";

import { decryptDataEnvelope, decryptTextField, importDek, unwrapDek, zeroFill } from "./crypto";
import {
  type DecryptedDigest,
  type DecryptedEntry,
  type DecryptedObservation,
  buildDataJson,
  mediaExtension,
  renderReadmeMarkdown,
  renderSchemaMarkdown,
} from "./dataJson";
import {
  fetchComments,
  fetchDigests,
  fetchEntries,
  fetchFamily,
  fetchMemberships,
  fetchObservations,
  fetchPersons,
  fetchReactions,
} from "./firestoreFetch";
import { fetchEncryptedMedia } from "./storageFetch";
import type { ZipEntry } from "./zipStream";
import type {
  CommentEnvelope,
  EntryDoc,
  ExportComment,
  ExportReaction,
  PersonDoc,
  ReactionEnvelope,
} from "../types";

export interface ExportProgress {
  phase: "metadata" | "media" | "finalizing";
  entriesDone: number;
  entriesTotal: number;
  observationsDone: number;
  digestsDone: number;
  mediaDone: number;
  mediaTotal: number;
  mediaBytes: number;
  currentMediaPath?: string;
}

export interface ExportRunnerOptions {
  firestore: Firestore;
  storage: FirebaseStorage;
  familyId: string;
  kek: CryptoKey;
  onProgress?: (progress: ExportProgress) => void;
  /** Cancellation hook. Caller can flip this to abort mid-run. */
  signal?: AbortSignal;
}

class AbortedError extends Error {
  constructor() {
    super("export aborted");
    this.name = "AbortedError";
  }
}

function checkAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new AbortedError();
}

/**
 * @what: 1 Entry 配下のスタンプ (Reaction) を取得・復号し、平文展開した
 *   `ExportReaction[]` を返す (epic_18 task_459)。
 * @why: スタンプは世代を跨ぐ家族アセット (heirloom)。export に平文展開で含めて
 *   データロックインを避ける。ただし ZK 原則により **平文 person_id は出さず**、
 *   `actor_display_hint` には envelope 内の `author_role_snapshot` のみを載せる。
 * @failure: envelope の DEK unwrap / 復号失敗は CryptoError として伝播し、
 *   runner 全体が中止する (部分破損 export を作らない既定方針)。
 */
// @failure: envelope 復号失敗は CryptoError として throw され runner 全体を中止する
//   (decryptEntryComments と共通。reaction は tombstone 概念が無く全件が復号対象)。
async function decryptEntryReactions(
  firestore: Firestore,
  familyId: string,
  entryId: string,
  kek: CryptoKey,
  signal?: AbortSignal,
): Promise<ExportReaction[]> {
  const out: ExportReaction[] = [];
  for await (const batch of fetchReactions(firestore, familyId, entryId)) {
    for (const r of batch) {
      checkAborted(signal);
      const dekBytes = await unwrapDek({
        wrappedDekCiphertextBase64: r.wrapped_dek.ciphertext,
        kek,
      });
      let dek: CryptoKey;
      try {
        dek = await importDek(dekBytes);
      } finally {
        // importDek が throw しても生鍵をメモリに残さない (ZK)。
        zeroFill(dekBytes);
      }
      const plain = await decryptTextField({ ciphertextBase64: r.envelope, dek });
      const env = JSON.parse(plain) as ReactionEnvelope;
      out.push({
        kind: env.reaction_kind,
        // ZK: 平文 person_id (env.actor_person_id) は意図的に export へ含めない。
        actor_display_hint: env.author_role_snapshot,
        created_at: r.created_at,
      });
    }
  }
  return out;
}

/**
 * @what: 1 Entry 配下のコメント (Comment) を取得・復号し、平文展開した
 *   `ExportComment[]` を返す (epic_18 task_459)。
 * @why: 家族の会話も heirloom アセット。export に平文展開で含める。Firestore の
 *   `text` field は envelope ciphertext で、復号後 JSON の中の `text` が本文。
 *   actor は `author_role_snapshot` のみ (平文 person_id は出さない)。
 * @failure: tombstone (deletion_status=="deleted" / wrapped_dek 欠落) は本文・鍵が
 *   破棄済みのため復号せず `{text: null, actor_display_hint: null, deleted_at}` で
 *   記録する。それ以外の復号失敗は CryptoError として伝播し runner 全体を中止する。
 */
async function decryptEntryComments(
  firestore: Firestore,
  familyId: string,
  entryId: string,
  kek: CryptoKey,
  signal?: AbortSignal,
): Promise<ExportComment[]> {
  const out: ExportComment[] = [];
  for await (const batch of fetchComments(firestore, familyId, entryId)) {
    for (const c of batch) {
      checkAborted(signal);
      const isTombstone = c.deletion_status === "deleted" || !c.wrapped_dek || !c.text;
      if (isTombstone) {
        out.push({
          id: c.id,
          text: null,
          actor_display_hint: null,
          created_at: c.created_at,
          deleted_at: c.deleted_at ?? null,
          corrects: c.corrects,
          thread_id: c.thread_id,
        });
        continue;
      }
      const dekBytes = await unwrapDek({
        wrappedDekCiphertextBase64: c.wrapped_dek!.ciphertext,
        kek,
      });
      let dek: CryptoKey;
      try {
        dek = await importDek(dekBytes);
      } finally {
        // importDek が throw しても生鍵をメモリに残さない (ZK)。
        zeroFill(dekBytes);
      }
      const plain = await decryptTextField({ ciphertextBase64: c.text!, dek });
      const env = JSON.parse(plain) as CommentEnvelope;
      out.push({
        id: c.id,
        text: env.text,
        // ZK: 平文 person_id (env.actor_person_id) は意図的に export へ含めない。
        actor_display_hint: env.author_role_snapshot,
        created_at: c.created_at,
        // 編集チェーンの系譜 (UUID・非 PII)。最新版判別を export consumer 側で復元可能にする。
        corrects: c.corrects,
        thread_id: c.thread_id,
      });
    }
  }
  return out;
}

/**
 * @what: Run a full export against Firestore + Cloud Storage and yield zip
 *   entries in write order: media files first (one per `Entry.media[]`),
 *   then `data.json`, `schema.md`, `README.md`.
 * @why: 巨大なメディアを丸ごとメモリに保持しないために async iterator で
 *   1 ファイル単位ずつ呼び出し側 (`zipStream.streamZipToFile`) に渡す。
 *   メタデータ (data.json / Markdown) は最後に出すことで、README サマリーに
 *   実際の合計バイト数を埋め込める。
 * @failure: 例外を throw する条件:
 *   - `family` ドキュメントが存在しない
 *   - DEK アンラップ / data 復号 / fingerprint seed の不整合 (=> CryptoError)
 *   - Cloud Storage のメディア取得が 3 回リトライしても失敗 (MediaFetchError)
 *   - `signal.aborted` (AbortedError; UI 側で reset/regenerate された場合)
 *   いずれの例外も呼び出し側 (`useExportSession.startExport`) で catch して
 *   汎用カテゴリ (`disk_write_error` / `decryption_error` / `network_error`)
 *   に正規化される。1 件の record / media 失敗でも、整合性の崩れた zip を
 *   出力しないために全体を中止する設計を取る (対策 D / 対策 E と同精神)。
 */
export async function* runExport(
  options: ExportRunnerOptions,
): AsyncGenerator<ZipEntry, void, void> {
  const { firestore, storage, familyId, kek, onProgress, signal } = options;

  // --- Phase 1: metadata
  const family = await fetchFamily(firestore, familyId);
  if (!family) {
    throw new Error(`family not found: ${familyId}`);
  }

  const memberships: import("../types").FamilyMembershipDoc[] = [];
  for await (const batch of fetchMemberships(firestore, familyId)) {
    checkAborted(signal);
    memberships.push(...batch);
  }
  const persons: PersonDoc[] = await fetchPersons(
    firestore,
    memberships.map((m) => m.person_id),
  );

  // Entries (need to keep raw docs so we can later iterate media + observations)
  const entriesRaw: EntryDoc[] = [];
  for await (const batch of fetchEntries(firestore, familyId)) {
    checkAborted(signal);
    entriesRaw.push(...batch);
  }

  const entries: DecryptedEntry[] = [];
  const observations: DecryptedObservation[] = [];
  let entriesDone = 0;
  for (const raw of entriesRaw) {
    checkAborted(signal);
    const dekBytes = await unwrapDek({
      wrappedDekCiphertextBase64: raw.wrapped_dek.ciphertext,
      kek,
    });
    const dekKey = await importDek(dekBytes);
    zeroFill(dekBytes);

    const text = await decryptTextField({
      ciphertextBase64: raw.text,
      dek: dekKey,
    });

    // epic_18 task_459: 各 Entry 配下のスタンプ / コメントを平文展開して nest する。
    // reaction / comment は固有 DEK を持つため entry DEK は再利用せず helper 内で unwrap する。
    const reactions = await decryptEntryReactions(firestore, familyId, raw.id, kek, signal);
    const comments = await decryptEntryComments(firestore, familyId, raw.id, kek, signal);
    entries.push({ ...raw, text, reactions, comments });

    for await (const obsBatch of fetchObservations(firestore, familyId, raw.id)) {
      for (const obs of obsBatch) {
        checkAborted(signal);
        // observations have their own wrapped_dek
        const obsDekBytes = await unwrapDek({
          wrappedDekCiphertextBase64: obs.wrapped_dek.ciphertext,
          kek,
        });
        const obsDek = await importDek(obsDekBytes);
        zeroFill(obsDekBytes);
        const obsText = await decryptTextField({
          ciphertextBase64: obs.text,
          dek: obsDek,
        });
        observations.push({ ...obs, text: obsText });
      }
    }

    entriesDone++;
    onProgress?.({
      phase: "metadata",
      entriesDone,
      entriesTotal: entriesRaw.length,
      observationsDone: observations.length,
      digestsDone: 0,
      mediaDone: 0,
      mediaTotal: 0,
      mediaBytes: 0,
    });
  }

  // Digests
  const digests: DecryptedDigest[] = [];
  for await (const batch of fetchDigests(firestore, familyId)) {
    for (const raw of batch) {
      checkAborted(signal);
      const dekBytes = await unwrapDek({
        wrappedDekCiphertextBase64: raw.wrapped_dek.ciphertext,
        kek,
      });
      const dek = await importDek(dekBytes);
      zeroFill(dekBytes);
      const text = await decryptTextField({
        ciphertextBase64: raw.text,
        dek,
      });
      digests.push({ ...raw, text });
    }
    onProgress?.({
      phase: "metadata",
      entriesDone: entriesRaw.length,
      entriesTotal: entriesRaw.length,
      observationsDone: observations.length,
      digestsDone: digests.length,
      mediaDone: 0,
      mediaTotal: 0,
      mediaBytes: 0,
    });
  }

  // --- Phase 2: media
  const mediaItems = entriesRaw.flatMap((e, i) =>
    e.media.map((m, idx) => ({ entry: e, entryIndex: i, mediaIndex: idx, item: m })),
  );
  const mediaTotal = mediaItems.length;
  let mediaDone = 0;
  let mediaBytes = 0;
  // Build a lookup so we don't re-import DEK per media (re-use entry DEK)
  const dekByEntryId = new Map<string, CryptoKey>();
  for (const raw of entriesRaw) {
    const dekBytes = await unwrapDek({
      wrappedDekCiphertextBase64: raw.wrapped_dek.ciphertext,
      kek,
    });
    const dek = await importDek(dekBytes);
    zeroFill(dekBytes);
    dekByEntryId.set(raw.id, dek);
  }

  for (const m of mediaItems) {
    checkAborted(signal);
    const ciphertext = await fetchEncryptedMedia(storage, m.item.storage_path);
    const dek = dekByEntryId.get(m.entry.id);
    if (!dek) throw new Error(`missing DEK for entry ${m.entry.id}`);
    const plaintext = await decryptDataEnvelope({
      envelope: ciphertext,
      dek,
    });
    zeroFill(ciphertext);
    const filename = `media/${m.entry.id}_${m.mediaIndex}.${mediaExtension(m.item)}`;
    mediaBytes += plaintext.byteLength;
    yield { name: filename, input: plaintext };
    mediaDone++;
    onProgress?.({
      phase: "media",
      entriesDone: entriesRaw.length,
      entriesTotal: entriesRaw.length,
      observationsDone: observations.length,
      digestsDone: digests.length,
      mediaDone,
      mediaTotal,
      mediaBytes,
      currentMediaPath: m.item.storage_path,
    });
  }

  // --- Phase 3: data.json + schema.md + README.md
  const dataJson = buildDataJson({
    family,
    persons,
    memberships,
    entries,
    observations,
    digests,
  });
  const summary = {
    entries: entries.length,
    observations: observations.length,
    digests: digests.length,
    persons: persons.length,
    mediaFiles: mediaTotal,
    mediaBytes,
    totalBytes: 0, // approximated; actual zip size is reported by zipStream
  };
  yield { name: "data.json", input: JSON.stringify(dataJson, null, 2) };
  yield { name: "schema.md", input: renderSchemaMarkdown() };
  yield { name: "README.md", input: renderReadmeMarkdown(summary) };
  onProgress?.({
    phase: "finalizing",
    entriesDone: entriesRaw.length,
    entriesTotal: entriesRaw.length,
    observationsDone: observations.length,
    digestsDone: digests.length,
    mediaDone,
    mediaTotal,
    mediaBytes,
  });
}
